import React, { useEffect, useState } from 'react';

import { Form, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import {
  uploadOrderToReturnPhoto,
  confirmOrderNotDelivered,
  confirmReturnToPickupFromConsumer,
} from './services';

import { setMessage } from '../toasts/toastSlice';
import { setOrdersModalConfig, setOrdersToDeliverFilters } from './orderSlice';

import InputFile from '../common/_inputs/InputFile';
import OrderModalWrapper from './OrderModalWrapper';
import { handleAxiosErrorMessage } from '../../utils';
import { Trans, useTranslation } from 'react-i18next';
import {
  ENTEL_RETIROS_HOGAR_REJECTION_COMMENT_TYPES,
  IS_CL_CONTEXT,
  NOTIFICATION_TYPES,
  REJECTION_COMMENT_TYPES,
} from '../../config/consts/commons';
import { ORDER_TYPE_PICKUP_AT_WARECLOUD } from '../../config/consts/orders';

const ENTEL_RETIROS_HOGAR = 'Entel Retiros Hogar';

const OrdersToReportModal = () => {
  const { t } = useTranslation(['system', 'description', 'button', 'title']);
  const { ordersToDeliverFilters, ordersModalConfig } = useSelector(
    (state) => state.orders
  );
  const { order } = ordersModalConfig;
  const isPickupType11 =
    order?.id_order_type === ORDER_TYPE_PICKUP_AT_WARECLOUD;
  const isEntelRetirosHogarOrder =
    IS_CL_CONTEXT && order?.ecommerce_name === ENTEL_RETIROS_HOGAR;

  const [temporalFile, setTemporalFile] = useState('');
  const [isFileCompressing, setIsFileCompressing] = useState(false);
  const [buttonLabel, setButtonLabel] = useState(
    t('confirm', { ns: 'button' })
  );

  const [commentType, setCommentType] = useState(1);
  const comments = {
    [REJECTION_COMMENT_TYPES.WRONG_ADDRESS]: t(
      'orders.rejected.modal.wrong_address',
      { ns: 'description' }
    ),
    [REJECTION_COMMENT_TYPES.ADDRESS_NOT_FOUND]: t(
      'orders.rejected.modal.address_not_found',
      { ns: 'description' }
    ),
    [REJECTION_COMMENT_TYPES.DANGEROUS_SECTOR]: t(
      'orders.rejected.modal.dangerous_sector',
      { ns: 'description' }
    ),
    [REJECTION_COMMENT_TYPES.CUSTOMER_NO_ANSWER]: t(
      'orders.rejected.modal.comment_customer_no_answer',
      {
        ns: 'description',
      }
    ),
    [REJECTION_COMMENT_TYPES.CUSTOMER_CANCELS_ORDER]: t(
      'orders.rejected.modal.customer_cancels_order',
      { ns: 'description' }
    ),
    [REJECTION_COMMENT_TYPES.CUSTOMER_NOT_KNOWN]: t(
      'orders.rejected.modal.customer_not_known',
      { ns: 'description' }
    ),
    [REJECTION_COMMENT_TYPES.CUSTOMER_DOESNT_KNOW_ORDER]: t(
      'orders.rejected.modal.customer_doesnt_know_order',
      {
        ns: 'description',
      }
    ),
    [ENTEL_RETIROS_HOGAR_REJECTION_COMMENT_TYPES.CUSTOMER_DISTRUSTS_PICKUP]: t(
      'orders.rejected.modal.customer_distrusts_pickup',
      { ns: 'description' }
    ),
    [ENTEL_RETIROS_HOGAR_REJECTION_COMMENT_TYPES.CUSTOMER_DELIVERED_TO_ENTEL_STORE]: t(
      'orders.rejected.modal.customer_will_deliver_or_delivered_to_store',
      { ns: 'description' }
    ),
    [REJECTION_COMMENT_TYPES.BRAND_CANCELS_ORDER]: t(
      'orders.rejected.modal.brand_cancels_order',
      { ns: 'description' }
    ),
    [REJECTION_COMMENT_TYPES.INSUFFICIENT_TIME]: t(
      'orders.rejected.modal.insufficient_time',
      { ns: 'description' }
    ),
  };
  const defaultRejectionCommentOptions = [
    REJECTION_COMMENT_TYPES.WRONG_ADDRESS,
    REJECTION_COMMENT_TYPES.ADDRESS_NOT_FOUND,
    REJECTION_COMMENT_TYPES.DANGEROUS_SECTOR,
    REJECTION_COMMENT_TYPES.CUSTOMER_NO_ANSWER,
    REJECTION_COMMENT_TYPES.CUSTOMER_CANCELS_ORDER,
    REJECTION_COMMENT_TYPES.CUSTOMER_NOT_KNOWN,
    REJECTION_COMMENT_TYPES.CUSTOMER_DOESNT_KNOW_ORDER,
    REJECTION_COMMENT_TYPES.BRAND_CANCELS_ORDER,
    REJECTION_COMMENT_TYPES.INSUFFICIENT_TIME,
  ];
  const entelRetirosHogarRejectionCommentOptions = [
    REJECTION_COMMENT_TYPES.WRONG_ADDRESS,
    REJECTION_COMMENT_TYPES.ADDRESS_NOT_FOUND,
    REJECTION_COMMENT_TYPES.DANGEROUS_SECTOR,
    REJECTION_COMMENT_TYPES.CUSTOMER_NO_ANSWER,
    REJECTION_COMMENT_TYPES.CUSTOMER_CANCELS_ORDER,
    REJECTION_COMMENT_TYPES.CUSTOMER_NOT_KNOWN,
    REJECTION_COMMENT_TYPES.CUSTOMER_DOESNT_KNOW_ORDER,
    ENTEL_RETIROS_HOGAR_REJECTION_COMMENT_TYPES.CUSTOMER_DISTRUSTS_PICKUP,
    ENTEL_RETIROS_HOGAR_REJECTION_COMMENT_TYPES.CUSTOMER_DELIVERED_TO_ENTEL_STORE,
    REJECTION_COMMENT_TYPES.INSUFFICIENT_TIME,
  ];
  const rejectionCommentOptions = isEntelRetirosHogarOrder
    ? entelRetirosHogarRejectionCommentOptions
    : defaultRejectionCommentOptions;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    setCommentType(REJECTION_COMMENT_TYPES.WRONG_ADDRESS);
  }, [order?.id_order]);

  const handleSubmit = async () => {
    setShowSubmitError(false);

    const { id_order, order_code } = order;

    const formData = new FormData();
    formData.append('file', temporalFile);

    setIsSubmitting(true);
    try {
      const uploadResponse = await uploadOrderToReturnPhoto({
        id_order,
        formData,
      });

      const payload = {
        id_order,
        comment: comments[commentType],
        photo_link: uploadResponse.data,
      };

      if (isPickupType11) {
        await confirmReturnToPickupFromConsumer(payload);
      } else {
        await confirmOrderNotDelivered(payload);
      }

      resetModal();
      dispatch(setOrdersToDeliverFilters({ ...ordersToDeliverFilters }));

      dispatch(
        setMessage({
          body: isPickupType11
            ? t('ORDER_RETURN_TO_PICKUP_FROM_CONSUMER_SUCCESS', {
                ns: 'system',
                order: order_code,
              })
            : t('ORDER_REJECTED_SUCCESS', {
                ns: 'system',
                order: order_code,
              }),
          type: NOTIFICATION_TYPES.SUCCESS,
        })
      );
    } catch (err) {
      const errorMessage = handleAxiosErrorMessage(err);
      setIsSubmitting(false);
      setButtonLabel(t(errorMessage, { ns: 'system' }));
      setShowSubmitError(true);

      dispatch(
        setMessage({
          body: t('confirm_return', { ns: 'button' }),
          type: 'error',
        })
      );
    }
  };

  const resetModal = () => {
    const { show, ...rest } = ordersModalConfig;
    setTemporalFile(null);
    setButtonLabel(t('confirm', { ns: 'button' }));
    setCommentType(REJECTION_COMMENT_TYPES.WRONG_ADDRESS);
    setIsSubmitting(false);
    setShowSubmitError(false);
    dispatch(setOrdersModalConfig({ ...rest, show: false }));
  };

  return (
    <OrderModalWrapper
      handleClose={resetModal}
      handleSubmit={handleSubmit}
      buttonLabel={buttonLabel}
      submitButtonDisabled={!commentType || !temporalFile || isFileCompressing}
      isSubmitting={isSubmitting}
      showSubmitError={showSubmitError}
    >
      <Row className="my-3">
        {t('orders.rejected.modal.reason', { ns: 'title' })}
      </Row>
      {rejectionCommentOptions.map((commentValue, index) => (
        <Row key={commentValue}>
          <Form.Check
            type="radio"
            label={comments[commentValue]}
            id={`option-${index + 1}`}
            onChange={() => setCommentType(commentValue)}
            checked={commentType === commentValue}
          />
        </Row>
      ))}

      <InputFile
        label={
          isPickupType11
            ? t('orders.rejected.modal.image_label_pickup', {
                ns: 'description',
                defaultValue:
                  'Adjunta una foto que evidencie el intento de retiro',
              })
            : t('orders.rejected.modal.image_label', {
                ns: 'description',
              })
        }
        onFileReady={setTemporalFile}
        onProcessingChange={setIsFileCompressing}
        inputId="file-report-order"
      />
      {!isPickupType11 && (
        <p className="text- text-center p-2 m-3">
          <Trans
            t={t}
            i18nKey="orders.rejected.modal.not_deliver_today_warning"
            ns="description"
          >
            ⚠️ Remember that if you do not deliver the order today, you must
            return it <strong>before 11:30AM of the next business day.</strong>.
          </Trans>
        </p>
      )}
    </OrderModalWrapper>
  );
};

export default OrdersToReportModal;
